import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  IconButton,
  FavoriteBorderOutlinedIcon,
  ToolTip,
  Popover
} from '@esgian/esgianui';
import OverviewTab from './OverviewTab';
import VoyageTab from './VoyageTab';
import { PageLinkIcon } from '@components';
import { ContentPanel } from '@components/Maps/DashboardActivityMap';
import { useTheme, useNextPortCall, useLatestPortCalls, useFleets, useSegment } from '@hooks';
import { ProfileLink } from '@components/Links';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import CreateFleetModal from '../../../../../Maps/DashboardActivityMap/DashboardActivityMapActionWindow/CreateFleetModal';
import { toast } from 'react-toastify';
import AddToFleetPopup from '../../DashboardActivityMapFleetComponents/AddToFleetPopup';

// Fleet selection modal component
const SelectFleetModal = ({
  open,
  onClose,
  fleets,
  onSelectFleet,
  onCreateNewFleet,
  isLoading
}) => {
  const [selectedFleetId, setSelectedFleetId] = useState('');
  const { customScrollbar } = useTheme();

  const handleChange = (event) => {
    setSelectedFleetId(event.target.value);
  };

  const handleConfirm = () => {
    if (selectedFleetId === 'new') {
      onCreateNewFleet();
    } else {
      const selectedFleet = fleets.find((fleet) => fleet.id === selectedFleetId);
      if (selectedFleet) {
        onSelectFleet(selectedFleet);
      }
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ width: '100%', maxHeight: '80vh' }}>
        <Typography variant="h6" gutterBottom>
          Select Fleet
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Choose a fleet to add the vessel or create a new one
        </Typography>

        <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2, ...customScrollbar }}>
          <RadioGroup value={selectedFleetId} onChange={handleChange} sx={{ p: 2 }}>
            {fleets.map((fleet) => (
              <FormControlLabel
                key={fleet.id}
                value={fleet.id}
                control={<Radio />}
                label={
                  <Box component="span" display="flex" alignItems="center">
                    <Typography variant="body1">{fleet.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({fleet.vessels.length} vessels)
                    </Typography>
                  </Box>
                }
                sx={{ backgroundColor: 'background.paper' }}
              />
            ))}
            <FormControlLabel
              value="new"
              control={<Radio />}
              label={<Typography variant="body1">Create New Fleet</Typography>}
              sx={{ backgroundColor: 'background.paper' }}
            />
          </RadioGroup>
        </Paper>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={isLoading || (!selectedFleetId && selectedFleetId !== 'new')}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}>
            {isLoading ? 'Processing...' : 'Continue'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

SelectFleetModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fleets: PropTypes.array.isRequired,
  onSelectFleet: PropTypes.func.isRequired,
  onCreateNewFleet: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

export const portCallsTimePeriodOptions = [
  { label: '1 month', value: 1 },
  { label: '3 months', value: 3 },
  { label: '6 months', value: 6 }
];

const VesselDetailsWindow = ({ vesselImo, vesselsLatestPosition, onClose }) => {
  const { customScrollbar } = useTheme();
  const [isVoyageTabSelected, setIsVoyageTabSelected] = useState(false);
  const { segment } = useSegment();
  const { savedFleets, createFleet, isCreatingFleet, updateFleetVessels } = useFleets();

  const [showCreateFleetModal, setShowCreateFleetModal] = useState(false);
  const [isAddingToFleet, setIsAddingToFleet] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [selectedPortCallsTimePeriod, setSelectedPortCallsTimePeriod] = useState(
    portCallsTimePeriodOptions[0]
  );

  const { data: vesselLatestPortCalls = [], isLoadingLatestPortCalls } = useLatestPortCalls({
    imos: vesselImo ? [+vesselImo] : []
  });
  const { nextPortCall, isLoadingNextPortCall } = useNextPortCall(vesselImo);

  const selectedVesselLatestPosition = vesselsLatestPosition?.find(
    (vessel) => vessel.imo === +vesselImo
  );
  const selectedVesselNextPortCall = nextPortCall ?? {};

  const selectedVesselLatestPortCall = vesselLatestPortCalls?.[0] ?? {};
  const { uriExt } = useSegment();
  const navigate = useNavigate();

  // Filter fleets based on segment
  const segmentFilteredFleets = useMemo(() => {
    return savedFleets.filter((fleet) => fleet.segmentId === segment.id);
  }, [savedFleets, segment]);

  const startDate = moment().subtract(selectedPortCallsTimePeriod.value, 'month').startOf('month');
  let params = {
    departureDate: startDate.format('YYYY-MM-DDTHH:mm:ss')
  };
  let paramsEncoded = '';
  Object.keys(params).forEach((key) => {
    if (paramsEncoded !== '') {
      paramsEncoded += '&';
    }
    paramsEncoded += key + '=' + encodeURIComponent(params[key]);
  });

  const urlToNavigate = `${uriExt}/ongoing-voyages/${vesselImo}?${paramsEncoded}`;

  const handleClick = (e) => {
    e.preventDefault();
    navigate(urlToNavigate);
  };

  const renderTabContent = () => {
    if (isVoyageTabSelected) {
      return (
        <VoyageTab
          selectedVesselNextPortCall={selectedVesselNextPortCall}
          isLoadingNextPortCall={isLoadingNextPortCall}
          latestPortCalls={vesselLatestPortCalls}
          isLoadingLatestPortCalls={isLoadingLatestPortCalls}
          selectedPortCallsTimePeriod={selectedPortCallsTimePeriod}
          setSelectedPortCallsTimePeriod={setSelectedPortCallsTimePeriod}
        />
      );
    }

    return (
      <OverviewTab
        selectedVesselLatestPosition={selectedVesselLatestPosition}
        selectedVesselLatestPortCall={selectedVesselLatestPortCall}
        selectedVesselNextPortCall={selectedVesselNextPortCall}
      />
    );
  };

  const handleCreateFleet = async (values) => {
    try {
      setIsAddingToFleet(true);

      const fleetData = {
        ...values,
        color: values.color,
        segmentId: segment?.id
      };

      await createFleet(fleetData);

      setShowCreateFleetModal(false);

      setTimeout(() => {
        const newFleet = savedFleets.find((f) => f.name === values.name);
        if (newFleet) {
          addVesselToFleet(newFleet);
        }
      }, 2000);
    } catch (error) {
      console.error('Error creating fleet:', error);
      toast.error('Failed to create fleet. Please try again.');
    } finally {
      setIsAddingToFleet(false);
    }
  };

  const addVesselToFleet = async (fleet) => {
    if (!fleet || !selectedVesselLatestPosition) return;

    try {
      const vesselToAdd = {
        vesselName: selectedVesselLatestPosition.vesselName,
        vesselImo: selectedVesselLatestPosition.imo,
        operator:
          selectedVesselLatestPosition.operator ||
          selectedVesselLatestPosition.operatorShortName ||
          ''
      };

      await updateFleetVessels({
        fleetId: fleet.id,
        added: [vesselToAdd],
        removed: []
      });

      toast.success(`Added ${vesselToAdd.vesselName} to "${fleet.name}"`);
    } catch (error) {
      console.error('Error adding vessel to fleet:', error);
      toast.error('Failed to add vessel to the fleet. Please try again.');
    } finally {
      setIsAddingToFleet(false);
      setAnchorEl(null);
    }
  };

  const handleShowFleetSelection = (event) => {
    if (segmentFilteredFleets.length === 0) {
      // If no fleets exist yet, directly show create fleet modal
      setShowCreateFleetModal(true);
    } else {
      // Show the popover
      setAnchorEl(event.currentTarget);
    }
  };

  return (
    <ContentPanel
      title={
        <Box display="flex" alignItems="center" justifyContent="start" width="100%">
          <Typography variant="h6">{selectedVesselLatestPosition?.vesselName}</Typography>
          <ToolTip title="Add to Fleet" placement="bottom">
            <span>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleShowFleetSelection}
                disabled={!selectedVesselLatestPosition || isAddingToFleet}>
                {isAddingToFleet ? <CircularProgress size={16} /> : <FavoriteBorderOutlinedIcon />}
              </IconButton>
            </span>
          </ToolTip>
        </Box>
      }
      onClose={onClose}>
      <Box display="flex" flexDirection="column" gap={2} height="100%">
        <Box display="flex" gap={1}>
          <Button
            variant={isVoyageTabSelected ? 'outlined' : 'contained'}
            size="small"
            color={isVoyageTabSelected ? 'secondary' : 'primary'}
            sx={{ textTransform: 'none', fontSize: 12, p: 0.2 }}
            onClick={() => setIsVoyageTabSelected(false)}>
            Overview
          </Button>
          <Button
            size="small"
            variant={isVoyageTabSelected ? 'contained' : 'outlined'}
            color={isVoyageTabSelected ? 'primary' : 'secondary'}
            sx={{ textTransform: 'none', fontSize: 12, p: 0.2 }}
            onClick={() => setIsVoyageTabSelected(true)}>
            Voyage
          </Button>
        </Box>
        <Box
          display="flex"
          gap={2}
          flexDirection="column"
          flex={1}
          overflow="auto"
          sx={customScrollbar}>
          {renderTabContent()}
        </Box>
        <Box textAlign="center" mt="auto" display="flex" gap={2} justifyContent="center">
          {isVoyageTabSelected ? (
            <Button
              startIcon={<PageLinkIcon />}
              sx={{ textTransform: 'none', fontSize: 12, fontWeight: 'normal', color: 'black' }}
              onClick={handleClick}>
              {selectedVesselLatestPosition?.vesselName} voyage
            </Button>
          ) : (
            <Button startIcon={<PageLinkIcon />} sx={{ textTransform: 'none', fontSize: 12 }}>
              <ProfileLink
                name={selectedVesselLatestPosition?.vesselName}
                profile={'vessel'}
                id={vesselImo}
                textColor="black"
              />
            </Button>
          )}
        </Box>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}>
        <AddToFleetPopup
          savedFleets={segmentFilteredFleets}
          onCreateANewFleet={() => {
            setAnchorEl(null);
            setShowCreateFleetModal(true);
          }}
          onAddToFleet={addVesselToFleet}
          isAddingToFleet={isAddingToFleet}
        />
      </Popover>

      {/* Create Fleet Modal */}
      <CreateFleetModal
        open={showCreateFleetModal}
        onClose={() => setShowCreateFleetModal(false)}
        onSubmit={handleCreateFleet}
        isSubmitting={isCreatingFleet || isAddingToFleet}
      />
    </ContentPanel>
  );
};

VesselDetailsWindow.propTypes = {
  vesselImo: PropTypes.string.isRequired,
  vesselsLatestPosition: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func
};

export default VesselDetailsWindow;
